import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import { Checkbox } from '@xsolla/ui-core';

import { NumberInput, TagSelector } from '../../../ui';
import PersonalData from '../PersonalData';
import { ExtendedProjectConfiguration, Props } from '../types';
import {
  FieldValidationMap,
  PASSWORD,
  specialCharacters,
  USERNAME,
  validationTypes
} from '../utils';
import { CheckboxWrapper, TagSelectorWrapper } from './style';

const ValidationFields = ({ name }: Props): JSX.Element => {
  const intl = useIntl();
  const { validationRule } = FieldValidationMap[name];
  const { values } = useFormState<ExtendedProjectConfiguration>();
  const { change } = useForm<ExtendedProjectConfiguration>();
  const [redrawOptions, setRedrawOptions] = useState<boolean>(false);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, field: keyof ExtendedProjectConfiguration) => {
      const { name: fieldName, value } = event.target;

      if (values.validation_min_length && values.validation_max_length) {
        if (fieldName === USERNAME.MIN_CHARACTERS && Number(value) > values.validation_max_length) {
          return;
        }

        if (fieldName === USERNAME.MAX_CHARACTERS && Number(value) < values.validation_min_length) {
          return;
        }
      }

      change(field, Number(event.target.value));
    },
    [values.validation_min_length, values.validation_max_length]
  );

  const mapSpecialCharacters = useMemo(() => {
    return Object.keys(specialCharacters).map((value) => ({
      name: value,
      tagLabel: value,
      active: values.validation_spec_symbol ? values.validation_spec_symbol?.includes(value) : false
    }));
  }, [redrawOptions]);

  const onSpecialCharactersChange = (newValues: string[]) => {
    change('validation_spec_symbol', newValues);

    if (newValues.length === 0) {
      change('fields_validation', { ...values.fields_validation, spec_characters: false });
    } else {
      change('fields_validation', { ...values.fields_validation, spec_characters: true });
    }
  };

  const onSpecialCharactersCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name.includes(PASSWORD.SPEC_CHARACTERS)) {
      const { checked } = e.target;

      if (!checked) {
        change('validation_spec_symbol', []);
      }

      if (
        checked &&
        (!values.validation_spec_symbol || values.validation_spec_symbol.length === 0)
      ) {
        change('validation_spec_symbol', ['#$%&@^`~']);
      }

      setRedrawOptions((prevState) => !prevState);
    }
  };

  const renderCompactInput = (rule: string) => {
    const isMinCharacters = rule === USERNAME.MIN_CHARACTERS;

    return (
      <NumberInput
        compact
        inline
        name={isMinCharacters ? USERNAME.MIN_CHARACTERS : USERNAME.MAX_CHARACTERS}
        min={isMinCharacters ? 6 : 8}
        max={isMinCharacters ? 30 : 50}
        onChange={(event) =>
          onChange(event, isMinCharacters ? 'validation_min_length' : 'validation_max_length')
        }
        value={isMinCharacters ? values.validation_min_length : values.validation_max_length}
      />
    );
  };

  // ToDo: remove after implementing password dictionary and personal data
  const DISABLED_FIELDS = [PASSWORD.PERSONAL_DATA, PASSWORD.DICTIONARY];

  return (
    <>
      {validationRule.map((rule) => {
        const { componentType } = validationTypes[rule];

        return (
          <CheckboxWrapper key={`fields_validation.${rule}`}>
            <Field name={`fields_validation.${rule}`} type="checkbox">
              {({ input }) => (
                <>
                  <Checkbox
                    disabled={DISABLED_FIELDS.includes(rule as PASSWORD)}
                    {...input}
                    onChange={(e) => {
                      onSpecialCharactersCheckboxChange(e);
                      input.onChange(e);
                    }}>
                    {intl.formatMessage(
                      {
                        id: `configuration-page.validation-rule.${rule}`
                      },
                      {
                        number: renderCompactInput(rule),
                        a: (chunk: string) => <span>{chunk}</span>
                        // ToDo: uncomment and replace after implementing password dictionary
                        // a: (chunk: string) => (
                        //   <LinkStyles>
                        //     <a
                        //       href="https://nordpass.com/most-common-passwords-list/"
                        //       target="_blank"
                        //       rel="noreferrer">
                        //       {chunk}
                        //     </a>
                        //   </LinkStyles>
                        // )
                      }
                    )}
                  </Checkbox>
                </>
              )}
            </Field>
            {componentType === 'select' && (
              <TagSelectorWrapper>
                <TagSelector options={mapSpecialCharacters} onChange={onSpecialCharactersChange} />
              </TagSelectorWrapper>
            )}
            {componentType === 'extraData' && (
              <TagSelectorWrapper>
                <PersonalData />
              </TagSelectorWrapper>
            )}
          </CheckboxWrapper>
        );
      })}
    </>
  );
};

export default ValidationFields;
